@if (open) {
  <div class="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4">
    <div class="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"></div>

    <div class="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92dvh] sm:max-h-[90vh] flex flex-col">
      <div class="sm:hidden mx-auto mt-3 h-1 w-10 rounded-full bg-gray-300 flex-shrink-0"></div>
      <!-- Header -->
      <div class="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200">
        <div>
          <h2 class="text-lg font-semibold text-gray-900">{{ title }}</h2>
          <p class="text-xs text-gray-500 mt-0.5">
            Members are shared across the tenant. Assign to one or more committees below.
          </p>
        </div>
        <button type="button" (click)="close()"
                class="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Body -->
      <form [formGroup]="form" (ngSubmit)="onSubmit()" class="overflow-y-auto overscroll-contain px-4 sm:px-6 py-5 space-y-5 flex-1">

        @if (errorMessage) {
          <div class="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
            {{ errorMessage }}
          </div>
        }

        <!-- Section: Personal Info -->
        <section>
          <h3 class="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <span class="w-1 h-4 bg-blue-500 rounded"></span> Personal Info
          </h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div class="sm:col-span-2 lg:col-span-1">
              <label class="block text-xs font-medium text-gray-700 mb-1">Full Name <span class="text-red-500">*</span></label>
              <input type="text" formControlName="fullName" placeholder="e.g. Kamal Perera"
                     class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
              @if (f['fullName'].invalid && f['fullName'].touched) {
                <p class="text-xs text-red-600 mt-1">Full name is required (min 2 characters).</p>
              }
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">NIC <span class="text-red-500">*</span></label>
              <input type="text" formControlName="nic" placeholder="123456789V or 200012345678"
                     class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
              @if (f['nic'].pending) {
                <p class="text-xs text-gray-500 mt-1">Checking NIC…</p>
              } @else if (f['nic'].errors?.['taken'] && f['nic'].touched) {
                <p class="text-xs text-red-600 mt-1">This NIC is already used by another member in this tenant.</p>
              } @else if (f['nic'].invalid && f['nic'].touched) {
                <p class="text-xs text-red-600 mt-1">NIC is required.</p>
              }
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Date of Birth</label>
              <input type="date" formControlName="dateOfBirth"
                     class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Gender</label>
              <select formControlName="gender"
                      class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option [ngValue]="null">— Select —</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Occupation</label>
              <input type="text" formControlName="occupation" placeholder="e.g. Farmer, Teacher"
                     class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
          </div>
        </section>

        <!-- Section: Contact -->
        <section>
          <h3 class="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <span class="w-1 h-4 bg-blue-500 rounded"></span> Contact
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Phone <span class="text-red-500">*</span></label>
              <input type="tel" formControlName="phone" placeholder="+94 77 9876543"
                     class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
              @if (f['phone'].pending) {
                <p class="text-xs text-gray-500 mt-1">Checking phone…</p>
              } @else if (f['phone'].errors?.['taken'] && f['phone'].touched) {
                <p class="text-xs text-red-600 mt-1">This phone is already used by another member in this tenant.</p>
              } @else if (f['phone'].invalid && f['phone'].touched) {
                <p class="text-xs text-red-600 mt-1">Phone is required.</p>
              }
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Email</label>
              <input type="email" formControlName="email" placeholder="kamal&#64;example.com"
                     class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
              @if (f['email'].invalid && f['email'].touched) {
                <p class="text-xs text-red-600 mt-1">Invalid email format.</p>
              }
            </div>
            <div class="md:col-span-2">
              <label class="block text-xs font-medium text-gray-700 mb-1">Address</label>
              <textarea formControlName="address" rows="2" placeholder="45 Temple Road, Maharagama"
                        class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
            </div>
          </div>
        </section>

        <!-- Section: Location -->
        <section>
          <h3 class="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <span class="w-1 h-4 bg-blue-500 rounded"></span> Location
          </h3>
          <div class="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-3 py-3 bg-gray-50">
            <div class="flex items-center gap-3 min-w-0">
              <div class="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                <svg class="w-5 h-5" [class.text-blue-600]="location" [class.text-gray-400]="!location" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
              </div>
              <div class="min-w-0">
                @if (location) {
                  <p class="text-sm font-medium text-gray-900 truncate">{{ location.formattedAddress || 'Location set' }}</p>
                  <p class="text-xs text-gray-500 font-mono">{{ location.latitude }}, {{ location.longitude }}</p>
                } @else {
                  <p class="text-sm text-gray-500">No location set</p>
                  <p class="text-xs text-gray-400">Pin the member's home on the map.</p>
                }
              </div>
            </div>
            <div class="flex items-center gap-2 flex-shrink-0">
              @if (location) {
                <button type="button" (click)="clearLocation()"
                        class="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors">Clear</button>
              }
              <button type="button" (click)="openLocationModal()"
                      class="px-3 py-1.5 text-xs font-medium text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors">
                {{ location ? 'Change' : 'Set location on map' }}
              </button>
            </div>
          </div>
        </section>

        <!-- Section: Household -->
        <section>
          <h3 class="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <span class="w-1 h-4 bg-blue-500 rounded"></span> Household
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Household ID</label>
              <input type="text" formControlName="householdId" placeholder="e.g. HH-001"
                     class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
              <p class="text-xs text-gray-400 mt-1">Use the same ID for everyone in the same house.</p>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Household Role</label>
              <select formControlName="householdRole"
                      class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option [ngValue]="null">— Select —</option>
                <option value="Head">Head</option>
                <option value="Spouse">Spouse</option>
                <option value="Child">Child</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Emergency Contact Name</label>
              <input type="text" formControlName="emergencyContactName" placeholder="e.g. Nimal Perera"
                     class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Emergency Contact Phone</label>
              <input type="tel" formControlName="emergencyContactPhone" placeholder="+94 77 1234567"
                     class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
          </div>
        </section>

        <!-- Section: Committees (create only — edit via the member details view) -->
        @if (!isEditMode) {
          <section>
            <h3 class="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <span class="w-1 h-4 bg-blue-500 rounded"></span> Committees <span class="text-red-500">*</span>
            </h3>
            <p class="text-xs text-gray-500 mb-2">Pick every committee this member belongs to. You can change this later from the member's details view.</p>
            <div class="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
              @for (unit of activeUnits; track unit.id) {
                <div class="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                     (click)="toggleBu(unit.id)">
                  <div class="w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                       [class.bg-blue-600]="isBuSelected(unit.id)"
                       [class.border-blue-600]="isBuSelected(unit.id)"
                       [class.border-gray-300]="!isBuSelected(unit.id)">
                    @if (isBuSelected(unit.id)) {
                      <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                      </svg>
                    }
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-gray-900 truncate">{{ unit.name }}</p>
                    @if (unit.description) {
                      <p class="text-xs text-gray-500 truncate">{{ unit.description }}</p>
                    }
                  </div>
                </div>
              }
              @if (activeUnits.length === 0) {
                <div class="text-center text-gray-400 text-sm py-4">No active committees available</div>
              }
            </div>
          </section>
        }

        <!-- Section: Membership -->
        <section>
          <h3 class="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <span class="w-1 h-4 bg-blue-500 rounded"></span> Membership
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Joined Date <span class="text-red-500">*</span></label>
              <input type="date" formControlName="joinedDate"
                     class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Monthly Fee</label>
              <input type="number" step="0.01" min="0" formControlName="monthlyFee"
                     placeholder="leave blank for default"
                     class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select formControlName="status"
                      class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Left">Left</option>
              </select>
            </div>
            <div class="md:col-span-3">
              <label class="block text-xs font-medium text-gray-700 mb-1">Notes</label>
              <textarea formControlName="notes" rows="2" placeholder="Any additional information..."
                        class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"></textarea>
            </div>
          </div>
        </section>

        @if (!isEditMode) {
          <!-- Section: Login account (auto-provisioned) -->
          <section class="rounded-xl border border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 flex items-start gap-3">
            <div class="w-9 h-9 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 11c0-1.1.9-2 2-2s2 .9 2 2v2H8v-2c0-1.1.9-2 2-2zm-5 6h10v4H5v-4zm5-12a4 4 0 100 8 4 4 0 000-8z"/>
              </svg>
            </div>
            <div class="flex-1">
              <h3 class="text-sm font-semibold text-gray-800">Login account will be created</h3>
              <p class="text-[11px] text-gray-600 leading-relaxed mt-0.5">
                A 4-digit temporary password will be sent via SMS to the member's phone (and email, if provided).
                The member can sign in with their phone number, NIC, or email and will be asked to change the password on first login.
              </p>
            </div>
          </section>
        }

        <app-custom-fields-input #cf formKey="Member"
          [businessUnitId]="customFieldsBuId"
          [value]="member?.customFields || {}"></app-custom-fields-input>
      </form>

      <!-- Footer -->
      <div class="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 px-4 sm:px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-4 border-t border-gray-200 bg-gray-50 sm:rounded-b-2xl">
        <button type="button" (click)="close()" [disabled]="isSubmitting"
                class="w-full sm:w-auto max-md:tap-target px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-white transition-colors disabled:opacity-50">
          Cancel
        </button>
        <button type="button" (click)="onSubmit()" [disabled]="isSubmitting"
                class="w-full sm:w-auto max-md:tap-target inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50">
          <app-spinner *ngIf="isSubmitting" size="xs" colour="white"></app-spinner>
          {{ isSubmitting ? 'Saving…' : (isEditMode ? 'Update Member' : 'Create Member') }}
        </button>
      </div>
    </div>
  </div>

  <!-- Location picker (separate modal, stacks above) -->
  <app-location-picker-modal
    [open]="locationModalOpen"
    [value]="location"
    (closed)="closeLocationModal()"
    (confirmed)="onLocationConfirmed($event)">
  </app-location-picker-modal>
}
